import { useEffect } from "react";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

// Reads admin-managed tracking scripts from settings/tracking and injects them
// site-wide ONCE per load — headerScripts before </head>, bodyScripts before
// </body>. Used for Google Tag Manager / Analytics / tracking pixels, so it
// deliberately runs a single time (not per route) to avoid re-initialising
// analytics on every SPA navigation.
const ATTR = "data-managed-tracking";

function inject(parent, html) {
  if (!html || !html.trim()) return;
  // Re-create <script> nodes from a template so they actually execute
  // (scripts added via innerHTML never run).
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  tpl.content.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.textContent = old.textContent;
    old.replaceWith(s);
  });
  [...tpl.content.childNodes].forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) node.setAttribute(ATTR, "1");
    parent.appendChild(node);
  });
}

export default function useSiteTracking() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "tracking"));
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        // Clear anything we injected before (e.g. on a hot reload).
        document.querySelectorAll(`[${ATTR}]`).forEach((n) => n.remove());
        inject(document.head, data.headerScripts);
        inject(document.body, data.bodyScripts);
      } catch {
        /* tracking is best-effort; never block the page */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
